import React from 'react';
import { motion } from 'framer-motion';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, 
  BarChart, Bar, XAxis, YAxis, Tooltip 
} from 'recharts';
import { 
  TrendingUp, Award, Calendar, Zap,
  Target, BookOpen, Dumbbell, PhoneOff, Brain, Coffee
} from 'lucide-react';
import { useDisciplineStore } from '../../contexts/disciplineStore';

const DisciplineProgressChart = ({ 
  data, 
  chartType = 'overview', // overview, category, streak, daily
  isDarkMode = true 
}) => {
  // Get discipline data from Zustand store
  const xp = useDisciplineStore(state => state.xp);
  const completedGoals = useDisciplineStore(state => state.completedGoals);
  const streak = useDisciplineStore(state => state.streak);
  const goals = useDisciplineStore(state => state.goals);
  // Format data for category distribution chart
  const formatCategoryData = () => {
    const categoryCount = {
      productivity: { name: 'Üretkenlik', count: 0, color: '#6366f1' },
      health: { name: 'Sağlık', count: 0, color: '#10b981' },
      learning: { name: 'Öğrenme', count: 0, color: '#f59e0b' },
      digital_detox: { name: 'Dijital Detoks', count: 0, color: '#ef4444' },
      mindfulness: { name: 'Farkındalık', count: 0, color: '#8b5cf6' },
      habit: { name: 'Alışkanlık', count: 0, color: '#ec4899' },
    };
    
    data.forEach(goal => {
      if (categoryCount[goal.category]) {
        categoryCount[goal.category].count++;
      }
    });
    
    return Object.values(categoryCount).filter(cat => cat.count > 0);
  };
  
  // Format data for completion rate chart
  const formatCompletionData = () => {
    const totalGoals = data.length;
    const completedGoals = data.filter(goal => goal.completed).length;
    const inProgressGoals = data.filter(goal => !goal.completed && goal.progress > 0).length;
    const notStartedGoals = data.filter(goal => !goal.completed && goal.progress === 0).length;
    
    return [
      { name: 'Tamamlanan', value: completedGoals, color: '#10b981' },
      { name: 'Devam Eden', value: inProgressGoals, color: '#6366f1' },
      { name: 'Başlanmayan', value: notStartedGoals, color: '#9ca3af' }
    ].filter(item => item.value > 0);
  };
  
  // Format data for daily activity chart
  const formatDailyData = () => {
    // Generate last 7 days
    const days = [];
    const dayNames = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dayIndex = date.getDay();
      days.push({
        date: date.toISOString().split('T')[0],
        name: dayNames[dayIndex === 0 ? 6 : dayIndex - 1],
        completed: 0,
        total: 0
      });
    }
    
    // Count completions for each day
    data.forEach(goal => {
      if (goal.type === 'habit' && goal.dailyProgress) {
        goal.dailyProgress.forEach(progress => {
          if (progress.date) {
            const day = days.find(d => d.date === progress.date.split('T')[0]);
            if (day) {
              day.total++;
              if (progress.completed) {
                day.completed++;
              }
            }
          }
        });
      }
    });
    
    // Calculate completion percentage
    return days.map(day => ({
      ...day,
      rate: day.total > 0 ? (day.completed / day.total) * 100 : 0
    }));
  };
  
  const renderCategoryIcon = (category) => {
    switch (category) {
      case 'productivity': return <Target size={14} />;
      case 'health': return <Dumbbell size={14} />;
      case 'learning': return <BookOpen size={14} />;
      case 'digital_detox': return <PhoneOff size={14} />;
      case 'mindfulness': return <Brain size={14} />;
      case 'habit': return <Coffee size={14} />;
      default: return <Target size={14} />;
    }
  };
    // Render XP progress bar
  const renderXPProgress = () => {
    // Calculate level based on XP (simple formula: level = 1 + Math.floor(xp / 100))
    const level = 1 + Math.floor(xp / 100);
    const levelProgress = (xp % 100) / 100;
    
    return (
      <div className="px-4 py-3">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center">
            <div className={`p-1.5 rounded-lg mr-2 ${isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-100'}`}>
              <Zap size={14} className={isDarkMode ? 'text-yellow-400' : 'text-yellow-600'} />
            </div>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              XP Seviyesi
            </span>
          </div>
          <div className="flex items-center">
            <motion.span 
              key={xp}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
            >
              {xp} XP
            </motion.span>
          </div>
        </div>
        
        <div className="flex items-center">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-2 ${
            isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'
          }`}>
            <span className="text-xs font-bold">{level}</span>
          </div>
          
          <div className="flex-1">
            <div className="w-full h-2 bg-gray-700/30 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-purple-500"
                initial={{ width: 0 }}
                animate={{ width: `${levelProgress * 100}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
              />
            </div>
            <div className="flex justify-end mt-1">
              <span className="text-xs opacity-70">Seviye {level+1} için {100 - (xp % 100)} XP kaldı</span>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Render the appropriate chart based on chartType
  const renderChart = () => {
    switch (chartType) {
      case 'category':
        return (
          <div className="p-4">
            <div className="flex items-center mb-4">
              <div className={`p-2 rounded-lg mr-3 ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                <Target size={18} className={isDarkMode ? 'text-blue-400' : 'text-blue-600'} />
              </div>
              <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Kategori Dağılımı
              </h3>
            </div>
            
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={formatCategoryData()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {formatCategoryData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                      borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                    }}
                    formatter={(value, name, props) => [`${value} hedef`, props.payload.name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            <div className="grid grid-cols-2 gap-2 mt-2">
              {formatCategoryData().map((entry, index) => (
                <div 
                  key={index} 
                  className="flex items-center"
                >
                  <div
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: entry.color }}
                  ></div>
                  <span className="text-xs">{entry.name}: {entry.count}</span>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 'overview':
        return (
          <div className="p-4">
            <div className="flex items-center mb-4">
              <div className={`p-2 rounded-lg mr-3 ${isDarkMode ? 'bg-green-900/30' : 'bg-green-100'}`}>
                <Award size={18} className={isDarkMode ? 'text-green-400' : 'text-green-600'} />
              </div>
              <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Hedef Tamamlama Oranı
              </h3>
            </div>
            
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={formatCompletionData()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {formatCompletionData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                      borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                    }}
                    formatter={(value, name, props) => [`${value} hedef`, props.payload.name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            <div className="grid grid-cols-3 gap-2 mt-2">
              {formatCompletionData().map((entry, index) => (
                <div 
                  key={index} 
                  className="flex items-center"
                >
                  <div
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: entry.color }}
                  ></div>
                  <span className="text-xs">{entry.name}: {entry.value}</span>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 'daily':
        return (
          <div className="p-4">
            <div className="flex items-center mb-4">
              <div className={`p-2 rounded-lg mr-3 ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
                <Calendar size={18} className={isDarkMode ? 'text-purple-400' : 'text-purple-600'} />
              </div>
              <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Günlük Aktivite
              </h3>
            </div>
            
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={formatDailyData()}
                  margin={{
                    top: 5, right: 30, left: 0, bottom: 5,
                  }}
                >
                  <XAxis 
                    dataKey="name" 
                    stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
                    fontSize={12}
                  />
                  <Tooltip
                    contentStyle={{ 
                      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                      borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                    }}
                    formatter={(value, name) => [`${value.toFixed(0)}%`, 'Tamamlama Oranı']}
                  />
                  <Bar 
                    dataKey="rate" 
                    fill="#8b5cf6" 
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      
      case 'streak':
        // Calculate current streaks
        const streakData = (() => {
          // This would be more complex in a real implementation
          // Here we'll return mock data
          return {
            current: 5,
            best: 12,
            total: 32,
          };
        })();
        
        return (
          <div className="p-4">
            <div className="flex items-center mb-4">
              <div className={`p-2 rounded-lg mr-3 ${isDarkMode ? 'bg-amber-900/30' : 'bg-amber-100'}`}>
                <Zap size={18} className={isDarkMode ? 'text-amber-400' : 'text-amber-600'} />
              </div>
              <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Hedef Serisi
              </h3>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <div className={`text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Güncel Seri
                </div>
                <div className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {streakData.current} gün
                </div>
              </div>
              
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <div className={`text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  En İyi Seri
                </div>
                <div className={`text-xl font-bold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                  {streakData.best} gün
                </div>
              </div>
              
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <div className={`text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Toplam
                </div>
                <div className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {streakData.total} hedef
                </div>
              </div>
            </div>
            
            <div className="flex items-center mt-5 space-x-1">
              {[...Array(7)].map((_, i) => (
                <div 
                  key={i}
                  className={`flex-1 h-2 rounded-full ${
                    i < streakData.current 
                      ? 'bg-amber-500' 
                      : isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <p className={`text-xs mt-2 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Son 7 gün
            </p>
          </div>
        );
        
      default:
        return <div>No chart available</div>;
    }
  };
    return (
    <motion.div
      className={`rounded-xl overflow-hidden ${isDarkMode ? 'bg-gray-900' : 'bg-white'} shadow-lg`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Always show XP progress at the top */}
      {renderXPProgress()}
      
      {/* Divider */}
      <div className={`h-px ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}></div>
      
      {/* Render the selected chart */}
      {renderChart()}
    </motion.div>
  );
};

export default DisciplineProgressChart;
